import { html, css } from 'lit';
import { QghBase } from './base.js';
import { isLive, scoreOrTime, translateCountry } from './utils.js';

export class QghTicker extends QghBase {
  static properties = {
    ...QghBase.properties,
    title: { type: String },
    theme: { type: String },
  };

  static styles = [
    ...QghBase.styles,
    css`
      :host {
        display: block;
        font-family: 'Roboto', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
        background: var(--qgh-bg);
        color: var(--qgh-color);
        border-radius: var(--qgh-radius);
      }
      .outer { display: flex; align-items: stretch; }
      .label {
        flex-shrink: 0; padding: 0 14px;
        display: flex; align-items: center;
        background: var(--qgh-label-bg); color: #fff;
        font-size: 11px; font-weight: 700;
        letter-spacing: .08em; text-transform: uppercase;
        white-space: nowrap; user-select: none;
      }
      .overflow { flex: 1; overflow: hidden; height: 40px; display: flex; align-items: center; }
      /* Override base .powered to be inline/compact for the ticker */
      .powered {
        border-top: none;
        border-left: 1px solid var(--qgh-border);
        padding: 0 10px;
        height: 40px;
        flex-shrink: 0;
      }
      .track {
        display: inline-flex; align-items: center; white-space: nowrap;
        animation: marquee linear infinite; will-change: transform;
      }
      .track:hover { animation-play-state: paused; }
      @keyframes marquee { from { transform: translateX(0) } to { transform: translateX(-50%) } }
      .item { font-size: 13px; padding: 0 18px; display: inline-flex; align-items: center; gap: 6px; }
      .item--live { color: var(--qgh-accent); }
      .item b { font-variant-numeric: tabular-nums; }
      .team-logo { width: 16px; height: 16px; object-fit: contain; flex-shrink: 0; }
      .sep { opacity: .25; font-size: 10px; }
      .empty { font-size: 12px; opacity: .4; padding: 0 16px; }
      .status-wrap {
        flex: 1; display: flex; flex-direction: column; min-width: 0;
      }
      .status-row {
        height: 40px; display: flex; align-items: center; padding: 0 16px;
        gap: 10px;
      }
      .status-logo { height: 18px; width: auto; filter: var(--qgh-logo-filter); opacity: .5; }
      .status-msg { font-size: 12px; opacity: .5; }
      .skeleton {
        height: 10px; border-radius: 4px;
        background: var(--qgh-border);
        animation: shimmer 1.4s ease-in-out infinite;
      }
      @keyframes shimmer { 0%,100%{opacity:.4} 50%{opacity:.9} }
    `,
  ];

  render() {
    if (this._loading) return html`
      <div class="outer">
        ${this.title ? html`<div class="label">${this.title}</div>` : ''}
        <div class="overflow">
          <div class="status-row">
            <div class="skeleton" style="width:40px"></div>
            <div class="skeleton" style="width:80px"></div>
            <div class="skeleton" style="width:60px"></div>
            <div class="skeleton" style="width:90px"></div>
            <div class="skeleton" style="width:50px"></div>
          </div>
        </div>
        ${this._poweredBy()}
      </div>`;

    if (this._error) return html`
      <div class="outer">
        ${this.title ? html`<div class="label">${this.title}</div>` : ''}
        <div class="overflow">
          <div class="status-row">
            <img class="status-logo" src="https://quienganahoy.com/svg/logo_qgh.svg" alt="">
            <span class="status-msg">No se pudo cargar</span>
          </div>
        </div>
        ${this._poweredBy()}
      </div>`;

    const dur   = Math.max(18, this._fixtures.length * 5);
    const items = () => this._fixtures.map(f => this._item(f));
    const track = this._fixtures.length
      ? html`<div class="track" style="animation-duration:${dur}s">${items()}<span aria-hidden="true">&nbsp;&nbsp;&nbsp;</span>${items()}</div>`
      : html`<span class="empty">Sin partidos</span>`;

    return html`
      <div class="outer">
        ${this.title ? html`<div class="label">${this.title}</div>` : ''}
        <div class="overflow">${track}</div>
        ${this._poweredBy()}
      </div>`;
  }

  _item(f) {
    const live  = isLive(f.fixture.status.short);
    const score = scoreOrTime(f);
    return html`
      <span class="item ${live ? 'item--live' : ''}">
        ${live ? html`<span class="dot"></span>` : ''}
        ${f.teams.home.logo ? html`<img class="team-logo" src="${f.teams.home.logo}" alt="" loading="lazy">` : ''}
        ${translateCountry(f.teams.home.name)}
        <b>${score}</b>
        ${f.teams.away.logo ? html`<img class="team-logo" src="${f.teams.away.logo}" alt="" loading="lazy">` : ''}
        ${translateCountry(f.teams.away.name)}
      </span>
      <span class="sep">·</span>`;
  }
}

